import { Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import AlertArchive from '../../../../shared/components/alert-archive/alert-archive';
import { useBreakpoint } from '../../../../shared/hooks/use-breakpoint';
import { usePagination } from '../../../../shared/hooks/use-pagination';
import { CheckIcon } from '../../../../shared/icons/check-icon';
import { DashIcon } from '../../../../shared/icons/dash-icon';
import { XIcon } from '../../../../shared/icons/x-icon';
import { convertServerDateTimeString } from '../../../../shared/utils/format-date/format-date';
import { useAppSelector } from '../../../../store/hooks';
import ArchiveFileUploadModal from '../../../archive/components/archive-file-upload-modal/archive-file-upload-modal';
import { useGetRequestQuery } from '../../../request/api/request-api';
import RequestPreviewSider from '../../../request/components/request-preview-sider/request-preview-sider';
import { selectActiveRequestId, setActiveRequestId } from '../../../request/store/requests-slice';
import ScanRequestCreateFormModal from '../../../scan-requests/components/scan-request-create-form-modal/scan-request-create-form-modal';
import { useGetTemplateFieldsQuery } from '../../../templates/api/templates-api';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { UserSettings } from '../../../user/models/user-settings';
import { useGetSignRequestsQuery } from '../../api/sign-requests-api';
import { SignRequestParams } from '../../models/sign-request-params';
import { SignRequestTableModel } from '../../models/sign-requests-table-model';
import {
  selectActiveSignRequestId,
  selectIsSignRequestPreviewOpen,
  setActiveSignRequestId,
  setIsSignRequestPreviewOpen,
  setSelectedSignRequestIds,
} from '../../store/sign-requests-slice';
import { buildSignRequestFiltersPayload } from '../../utils/build-sign-request-filters-payload';
import SignRequestArchiveModal from '../sign-request-archive-modal/sign-request-archive-modal';
import SignRequestCreateModal from '../sign-request-create-modal/sign-request-create-modal';
import SignRequestInPortalModal from '../sign-request-in-portal-modal/sign-request-in-portal-modal';
import SignRequestsExportModal from '../sign-requests-export-modal/sign-requests-export-modal';
import SignRequestsHeader from '../sign-requests-header/sign-requests-header';
import SignRequestsList from '../sign-requests-list/sign-requests-list';
import SignRequestsTable from '../sign-requests-table/sign-requests-table';
import styles from './sign-requests-content.module.css';

const getSearchFieldValue = (searchData: unknown, fieldName: string): unknown => {
  if (!searchData || Array.isArray(searchData) || typeof searchData !== 'object') {
    return undefined;
  }

  return (searchData as Record<string, unknown>)[fieldName];
};

const SignRequestsContent = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const screens = useBreakpoint();
  const activeRequestId = useAppSelector(selectActiveRequestId);
  const activeSignRequestId = useAppSelector(selectActiveSignRequestId);
  const isSignRequestPreviewOpen = useAppSelector(selectIsSignRequestPreviewOpen);
  const dispatch = useDispatch();
  const { data: userSettings } = useGetUserSettingsQuery();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { data: templateFieldsData } = useGetTemplateFieldsQuery(searchParams.get('template')!, {
    skip: !searchParams.get('template'),
  });
  const templateFields = useMemo(
    () => (searchParams.get('template') ? templateFieldsData : []),
    [searchParams, templateFieldsData],
  );

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { currentData: request, isFetching: isFetchingRequest } = useGetRequestQuery(activeRequestId!, {
    skip: !activeRequestId,
  });

  const [pageSize, setPageSize] = useState<number>();

  const signRequestsQueryParams = useMemo(
    () =>
      buildSignRequestFiltersPayload({
        searchParams,
        templateFields,
        overrides: {
          page: Number(searchParams.get('page')) || 1,
          limit: pageSize,
        },
      }) as SignRequestParams,
    [pageSize, searchParams, templateFields],
  );

  const unarchivedSignRequestsQueryParams = useMemo(
    () =>
      buildSignRequestFiltersPayload({
        searchParams,
        templateFields,
        overrides: {
          page: Number(searchParams.get('page')) || 1,
          limit: pageSize,
          archived: '0',
          canArchive: '1',
        },
      }) as SignRequestParams,
    [pageSize, searchParams, templateFields],
  );

  const { data: signRequests, isFetching: isFetchingSignRequests } = useGetSignRequestsQuery(signRequestsQueryParams, {
    skip: !pageSize,
  });

  const { data: unarchivedSignRequests } = useGetSignRequestsQuery(unarchivedSignRequestsQueryParams, {
    skip: !pageSize,
  });

  const totalRequests = useMemo(
    () =>
      pageSize && (signRequests?.items || []).length < pageSize
        ? pageSize * (signRequests?.page || 1)
        : signRequests?.total,
    [pageSize, signRequests],
  );

  const [limit, pagination] = usePagination(totalRequests, UserSettings.SignRequestsPageSize);

  const signRequestsDataSource: SignRequestTableModel[] = useMemo(() => {
    return (signRequests?.items || []).map(signRequest => {
      const requestDataChildren: SignRequestTableModel[] =
        signRequest.request.id === request?.request.id
          ? (request?.signRequests || [])
              .filter(requestSignRequest => requestSignRequest.id !== signRequest.id)
              .map(requestSignRequest => {
                return {
                  id: requestSignRequest.id,
                  key: `${signRequest.id}_${requestSignRequest.id}`,
                  name: requestSignRequest.name,
                  user: requestSignRequest.clientUser?.name || null,
                  customer: `${requestSignRequest.customer?.name || ''} ${requestSignRequest.customer?.surname || ''}`,
                  unit: requestSignRequest.unit,
                  status: requestSignRequest.status,
                  request: requestSignRequest.request,
                  documents: requestSignRequest.documents,
                  signedAt:
                    requestSignRequest.signedAt &&
                    convertServerDateTimeString(requestSignRequest.signedAt, userSettings?.[UserSettings.DateFormat]),
                  templateId: requestSignRequest.template?.id || null,
                  template: requestSignRequest.template?.name || null,
                  appVersion: requestSignRequest.appVersion,
                  isDeleted: requestSignRequest.deleted,
                  canArchive: requestSignRequest.canArchive,
                  archived: requestSignRequest.archived,
                  ...(templateFields &&
                    templateFields.reduce((accumulator, templateField) => {
                      const fieldValue = getSearchFieldValue(requestSignRequest.searchData, templateField.name);

                      return {
                        ...accumulator,
                        ...{
                          [templateField.label]:
                            templateField.type === 'boolean' ? (
                              fieldValue === undefined || fieldValue === null ? (
                                <DashIcon />
                              ) : fieldValue ? (
                                <CheckIcon />
                              ) : (
                                <XIcon />
                              )
                            ) : (
                              fieldValue
                            ),
                        },
                      };
                    }, {})),
                };
              })
          : [];

      return {
        id: signRequest.id,
        key: signRequest.id,
        name: signRequest?.name,
        user: signRequest?.clientUser?.name || null,
        unit: signRequest.unit,
        status: signRequest.status,
        request: signRequest.request,
        customer: `${signRequest.customer?.name || ''} ${signRequest.customer?.surname || ''}`,
        documents: signRequest.documents,
        signedAt:
          signRequest.signedAt &&
          convertServerDateTimeString(signRequest.signedAt, userSettings?.[UserSettings.DateFormat]),
        signRequestCount: signRequest.request.signCount || 0,
        scanRequestCount: signRequest.request.scanCount || 0,
        relatedFilesCount: signRequest.request.fileCount || 0,
        templateId: signRequest.template?.id || null,
        template: signRequest.template?.name || null,
        appVersion: signRequest.appVersion,
        isDeleted: signRequest.deleted,
        canArchive: signRequest.canArchive,
        archived: signRequest.archived,
        ...(templateFields &&
          templateFields.reduce((accumulator, templateField) => {
            const fieldValue = getSearchFieldValue(signRequest.searchData, templateField.name);

            return {
              ...accumulator,
              ...{
                [templateField.label]:
                  templateField.type === 'boolean' ? (
                    fieldValue === undefined || fieldValue === null ? (
                      <DashIcon />
                    ) : fieldValue ? (
                      <CheckIcon />
                    ) : (
                      <XIcon />
                    )
                  ) : (
                    fieldValue
                  ),
              },
            };
          }, {})),
        children:
          isFetchingRequest && signRequest.request.signCount > 1
            ? [
                {
                  key: `${signRequest.id}_spinner`,
                  name: <Spin />,
                },
              ]
            : requestDataChildren,
      };
    });
  }, [userSettings, signRequests, request, templateFields, isFetchingRequest]);

  useEffect(() => {
    setPageSize(limit);
  }, [limit]);

  const handleFilteringUnarchived = () => {
    setSearchParams(existingSearchParams => {
      existingSearchParams.set('archived', '0');
      existingSearchParams.set('canArchive', '1');
      return existingSearchParams;
    });
    dispatch(setSelectedSignRequestIds((unarchivedSignRequests?.items || []).map(signRequest => signRequest.id)));
  };

  return (
    <>
      {unarchivedSignRequests?.items && unarchivedSignRequests.items.length > 0 && (
        <AlertArchive onAction={handleFilteringUnarchived} />
      )}
      {signRequests && (
        <div className={styles.signRequests}>
          <SignRequestsHeader
            pagination={pagination}
            templateFields={templateFields || []}
            unarchivedSignRequests={unarchivedSignRequests?.items || []}
          />
          {screens.sm ? (
            <div className={styles.contentContainer}>
              <div className={styles.tableContainer}>
                <SignRequestsTable
                  isFetchingSignRequests={isFetchingSignRequests}
                  signRequestsDataSource={signRequestsDataSource}
                  templateFields={templateFields}
                />
              </div>
              <RequestPreviewSider
                collapsed={!activeSignRequestId || !isSignRequestPreviewOpen}
                onClose={() => {
                  dispatch(setActiveSignRequestId(null));
                  dispatch(setActiveRequestId(null));
                  dispatch(setIsSignRequestPreviewOpen(false));
                }}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                fullscreenUrl={Routes.SignRequest.replace(':signRequestId', activeSignRequestId!).replace(
                  ':requestId',
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  activeRequestId!,
                )}
              />
            </div>
          ) : (
            <SignRequestsList
              isFetchingSignRequests={isFetchingSignRequests}
              signRequestsDataSource={signRequestsDataSource}
            />
          )}
          <ScanRequestCreateFormModal />
        </div>
      )}
      <SignRequestCreateModal />
      <SignRequestsExportModal />
      <SignRequestArchiveModal />
      <SignRequestInPortalModal />
      <ArchiveFileUploadModal />
    </>
  );
};
export default SignRequestsContent;
